/**
 * 会议提交参数统一类型定义
 * 用于规范化提交时的参数检查和 InMeet 页面使用
 */

import { TemplateType } from '@/constants/templateTypes';
import { ScenarioType } from '@/constants/scenarioTypes';

/**
 * 输出类型 - 支持经典模板、场景模板和自定义模板
 */
export type OutputType = TemplateType | ScenarioType | string;

/**
 * 提交参数接口
 * 包含所有会议整理提交所需的字段
 */
export interface SubmitParams {
  /** 会议内容（必填）- 包含原始文本 + 附加的文件/音频内容 */
  Content: string;
  
  /** 行业领域（必填）- 如：企业服务、金融、科技等 */
  Industry: string;
  
  /** 输出类型（必填）- 模板类型：经典模板、场景模板或自定义 */
  Output_type: OutputType;
  
  /** 文本文件数量 */
  textNum?: string;
  
  /** 音频文件数量 */
  audioNum?: string;
  
  /** 是否有附件文件 - "true" 表示有，空字符串表示无 */
  files?: string;
  
  /** 自定义模板内容 - 当 Output_type 为 CUSTOM 时使用 */
  User_Define_Template?: string;
  
  /** 提交时间 */
  time?: string;
  
  /** 其他扩展字段 */
  [key: string]: any;
}

/**
 * 必填字段列表
 * 用于 checkSubmitParams 验证
 */
export const REQUIRED_SUBMIT_FIELDS: (keyof SubmitParams)[] = [
  'Industry',
  'Content',
  'Output_type',
];

/**
 * 验证提交参数
 * @param params 待验证的参数对象
 * @returns 验证结果对象
 */
export interface SubmitParamsValidationResult {
  isValid: boolean;
  missingFields: string[];
  errorMessage?: string;
}

/**
 * 创建空的提交参数对象
 */
export function createEmptySubmitParams(): Partial<SubmitParams> {
  return {
    Content: '',
    Industry: '',
    Output_type: '',
    textNum: '0',
    audioNum: '0',
    files: '',
    User_Define_Template: '',
  };
}

/**
 * 验证单个字段是否有效
 */
export function isFieldValid(value: any): boolean {
  return value !== undefined && value !== null && value !== '';
}

/**
 * 验证提交参数是否完整
 * @param params 待验证的参数
 * @param requiredFields 必填字段列表，默认使用 REQUIRED_SUBMIT_FIELDS
 * @returns 验证结果
 */
export function validateSubmitParams(
  params: Partial<SubmitParams>,
  requiredFields: (keyof SubmitParams)[] = REQUIRED_SUBMIT_FIELDS
): SubmitParamsValidationResult {
  const missingFields: string[] = [];
  
  for (const field of requiredFields) {
    if (!isFieldValid(params[field])) {
      missingFields.push(String(field));
    }
  }
  
  const isValid = missingFields.length === 0;
  
  return {
    isValid,
    missingFields,
    errorMessage: isValid ? undefined : `必填项为空: ${missingFields.join(', ')}`,
  };
}

/**
 * 快速检查提交参数（兼容原 checkAllParam 的用法）
 * @param params 待验证的参数
 * @param fields 需要检查的字段
 * @returns 是否全部有效
 */
export function checkSubmitParams(
  params: Partial<SubmitParams>,
  ...fields: (keyof SubmitParams)[]
): boolean {
  const fieldsToCheck = fields.length > 0 ? fields : REQUIRED_SUBMIT_FIELDS;
  return fieldsToCheck.every(field => isFieldValid(params[field]));
}
